export default function checkSolution(question, response, existingResult) {
  // if question is skipped, response will be null (which will break most checks) so set to undefined
  // so the check can override with default
  response = response === null ? undefined : response;
  switch (question.type) {
    case 'MultipleChoice':
      return checkMultipleChoice(question, response);
    case 'ShortAnswer':
      return checkAnswerInput(question.answer, question.answer_config, response).then(singlePointScore);
    case 'TableInputs':
      return checkTableInputs(question, response);
    case 'MultipleShortAnswers':
      return checkMultipleShortAnswers(question, response);
    case 'FillInTheBlank':
      return checkFITBAnswers(question, response);
    case 'MixMatch':
      return checkMixMatch(question, response);
    case 'AccountingJournalEntry':
      return checkAccountingJounalEntry(question, response);
    case 'MultiPartQuestion':
      return checkMultiPartQuestion(question, response, existingResult);
    case 'MarkYourself':
    default:
      return Promise.resolve(computeScore());
  }
}

export function computeScore(points, total) {
  if (!total) {
    return {
      total: 0,
      points: 0,
      score: 0,
    };
  }
  return {
    total,
    points,
    score: Number((points / total).toFixed(2)),
  };
}

function singlePointScore(isCorrect) {
  return {
    total: 1,
    points: isCorrect ? 1 : 0,
    score: isCorrect ? 1 : 0,
  };
}

function checkMixMatch(question, response = {}) {
  const pairings = question.pairings || [];
  const total = pairings.length;
  const points = Object.keys(response).filter(k => k === response[k]).length;
  return Promise.resolve(computeScore(points, total));
}

function checkAccountingJounalEntry(question, response) {
  const { transactions = [] } = question;
  let points = 0;
  let total = 0;
  transactions.forEach(txn => {
    total += 2;
    const responseTxns = (response || []).filter(t => t.type === txn.type && t.account === txn.account);
    // eslint-disable-next-line eqeqeq
    const responseTxn = responseTxns.find(t => t.amount == txn.amount) || responseTxns[0];
    if (responseTxn) {
      points++;
      // intentional == compare incase somehow saved as string
      // eslint-disable-next-line eqeqeq
      if (responseTxn.amount == txn.amount) {
        points++;
      }
    }
  });
  return Promise.resolve(computeScore(points, total));
}

function checkMultipleChoice(question, response) {
  const { allow_multiple } = question;
  const options = question.options || [];
  if (allow_multiple) {
    // const points = Math.max(
    //   options.map(o => (!!o.is_correct === (response || []).includes(o.id) ? 1 : -1)).reduce((a, b) => a + b, 0),
    //   0
    // );
    const points = response ? options.filter(o => !!o.is_correct === (response || []).includes(o.id)).length : 0;
    return Promise.resolve(computeScore(points, options.length));
  }
  const isCorrect = options
    .filter(o => o.is_correct)
    .map(o => o.id)
    .includes(response);
  return Promise.resolve(singlePointScore(isCorrect));
}

// Accept complete decimal/scientific numbers only. Do not evaluate expressions.
const DECIMAL_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
const NUMBER_PARTS = /^[+-]?(?<integer>\d*)(?:\.(?<fraction>\d*))?(?:e(?<exponent>[+-]?\d+))?$/i;
const PRECISION_KINDS = ['decimal_places', 'significant_figures'];

// answer_config.precision is {decimal_places: n} or {significant_figures: n}; anything else means no rule.
export function precisionRule(precision) {
  for (const kind of PRECISION_KINDS) {
    const raw = precision?.[kind];
    if (raw === undefined || raw === null || raw === '') continue;
    const count = Number(raw);
    if (Number.isInteger(count) && count >= (kind === 'decimal_places' ? 0 : 1)) return { kind, count };
  }
  return null;
}

function precisionLabel(rule) {
  const unit = rule.kind === 'decimal_places' ? 'decimal place' : 'significant figure';
  return `${rule.count} ${unit}${rule.count === 1 ? '' : 's'}`;
}

// Judged on the submission as written: "17.40" has two decimal places, "1.5e3" none (1500), "1.5e-2" three.
function meetsPrecision(value, rule) {
  const { exponent = '0', fraction = '', integer = '' } = NUMBER_PARTS.exec(value).groups;
  if (rule.kind === 'decimal_places') return Math.max(fraction.length - Number(exponent), 0) === rule.count;
  const digits = (integer + fraction).replace(/^0+/, '');
  if (!digits) return true; // zero has no measurable significant figures
  // Trailing zeros of a whole number written without a point are ambiguous: "1500" may carry 2, 3 or 4 figures.
  const ambiguousZeros = value.includes('.') ? 0 : digits.match(/0*$/)[0].length;
  return rule.count <= digits.length && rule.count >= digits.length - ambiguousZeros;
}

function checkNumericAnswer(answer, submission, margin = 0, rule = null) {
  const values = [answer, submission, margin ?? 0].map(value => String(value).trim());
  if (!values.every(value => DECIMAL_NUMBER.test(value))) return false;
  const [expected, actual, tolerance] = values.map(Number);
  if (![expected, actual, tolerance].every(Number.isFinite) || tolerance < 0) return false;
  if (rule && !meetsPrecision(values[1], rule)) return false;
  if (tolerance === 0) return expected === actual;
  // Only compensate for floating-point rounding, not a wider scientific margin.
  const rounding = 2 * Number.EPSILON * Math.max(Math.abs(expected), Math.abs(actual), tolerance);
  const distance = Math.abs(actual - expected);
  return distance <= tolerance || distance - tolerance <= rounding;
}

// A precision rule only makes sense on a plain decimal, so it routes editor questions to the strict grader too.
function strictNumeric(answerConfig) {
  const { margin_of_error, multiple, precision, strict_decimal, type } = answerConfig || {};
  if (type !== 'number' || multiple) return null;
  const rule = precisionRule(precision);
  return strict_decimal || rule ? { margin: margin_of_error, rule } : null;
}

// The message a student sees when the number is right but not written to the required precision.
export function precisionFeedback(answer, answerConfig, submission) {
  const strict = strictNumeric(answerConfig);
  if (!strict?.rule || checkNumericAnswer(answer, submission, strict.margin, strict.rule)) return null;
  if (!checkNumericAnswer(answer, submission, strict.margin)) return null;
  return `Give your answer to ${precisionLabel(strict.rule)}`;
}

export function checkAnswerInput(answerProp = '', answerConfig, submissionProp = '') {
  const {
    alt_answers,
    // is_equation,
    case_sensitive,
    margin_of_error,
    multiple,
    no_simplify_equation,
    strip_non_alphanumeric,
    type = 'text',
  } = answerConfig || {};
  const strict = strictNumeric(answerConfig);
  if (strict) return Promise.resolve(checkNumericAnswer(answerProp, submissionProp, strict.margin, strict.rule));
  if (!multiple && !answerProp) return Promise.resolve(true);
  if (type === 'boolean') return Promise.resolve(answerProp === submissionProp);
  // better to be in stand-alone function?
  const normalizeValue = input => {
    let normalizedValue = input
      .trim()
      .replace(/\s+/, ' ')
      .replace(/\u200B/g, ''); // \u200B is a non-zero whitespace not captured by \s
    if (!case_sensitive) normalizedValue = normalizedValue.toLowerCase();
    if (strip_non_alphanumeric && type === 'text') normalizedValue = normalizedValue.replace(/\W/g, '');
    if (type === 'equation') {
      normalizedValue = normalizedValue.replace(/(\\text{DNE})|(\bDNE\b)/gi, '\\nexists');
    }
    return normalizedValue;
  };
  const answer = normalizeValue(answerProp);
  const submission = normalizeValue(submissionProp);

  const hasAnswer = !!(multiple ? alt_answers && alt_answers.length : answer);
  if (!(hasAnswer && submission)) return Promise.resolve(false); // Must have answer and submission with content
  // if (multiple) return Promise.resolve((alt_answers || []).map(a => normalizeValue(a)).includes(submission));
  if (multiple) return Promise.resolve(alt_answers.some(a => normalizeValue(a) === submission));
  if (answer === submission) return Promise.resolve(true);

  if (type === 'equation') {
    if (answer.replace(/(\\)? /g, '') === submission.replace(/(\\)? /g, '')) return Promise.resolve(true);
    if (no_simplify_equation) return Promise.resolve(false);
    const eqNormSubmission = submission.replace(/(_|\^)\{\s*\}/g, '');
    return import(/* webpackChunkName: "kas" */ 'main/vendor/kas.js').then(({ KAS }) => {
      const answerExpr = KAS.parse(answer).expr;
      const submissionExpr = KAS.parse(eqNormSubmission).expr;
      return !!answerExpr && !!submissionExpr && KAS.compare(answerExpr, submissionExpr).equal;
    });
  }
  if (isFinite(answer) && margin_of_error) {
    const answerFloat = parseFloat(answer);
    const subAnswerFloat = parseFloat(submission);
    const marginOfError = parseFloat(margin_of_error);
    return Promise.resolve(
      answerFloat - marginOfError <= subAnswerFloat && answerFloat + marginOfError >= subAnswerFloat,
    );
  }
  // eslint-disable-next-line eqeqeq
  return Promise.resolve(submission == answer);
}

function checkTableInputs(question, response = {}) {
  const inputMatrix = question.input_matrix || [];
  const promises = [];
  const submissionResults = {};
  inputMatrix.forEach((row, i) => {
    row.forEach((cell, j) => {
      if (cell.type === 'input') {
        const answerKey = `${i}:${j}`;
        promises.push(
          checkAnswerInput(cell.value, cell.answer_config, response[answerKey]).then(
            res => (submissionResults[answerKey] = res),
          ),
        );
      }
    });
  });

  return Promise.all(promises).then(results => ({
    ...computeScore(results.filter(Boolean).length, results.length),
    breakdown: submissionResults,
  }));
}

function checkMultipleShortAnswers(question, response = {}) {
  const inputs = question.inputs;
  const promises = [];
  const submissionResults = {};
  (inputs || []).forEach(input => {
    promises.push(
      checkAnswerInput(input.answer, input.answer_config, response[input.id]).then(
        res => (submissionResults[input.id] = res),
      ),
    );
  });

  return Promise.all(promises).then(results => ({
    ...computeScore(results.filter(Boolean).length, results.length),
    breakdown: submissionResults,
  }));
}

function checkFITBAnswers(question, response = {}) {
  const blankInputs = Object.values(question.fitb_content.entityMap).filter(e => e.type === 'FILL_IN_THE_BLANK');
  const promises = [];
  const submissionResults = {};
  blankInputs.forEach(blankInput => {
    promises.push(
      checkAnswerInput(blankInput.data.value, null, response[blankInput.data.id]).then(
        res => (submissionResults[blankInput.data.id] = res),
      ),
    );
  });

  return Promise.all(promises).then(results => ({
    ...computeScore(results.filter(Boolean).length, results.length),
    breakdown: submissionResults,
  }));
}

function checkMultiPartQuestion(question, response = {}, existingResult) {
  // use existingResult data in case 1+ parts already checked with adjusted scores we don't want to overwrite
  const submissionResults = { ...existingResult?.breakdown };
  const promises = question.sub_questions?.map(sq => {
    const sqRes = submissionResults?.[sq.id];
    return sqRes ?
        Promise.resolve(sqRes)
      : checkSolution(sq, response[sq.id]).then(res => (submissionResults[sq.id] = res));
  });

  return Promise.all(promises || []).then(results => {
    let points = 0,
      total = 0;
    results.filter(Boolean).forEach(r => {
      points += r.points || 0;
      total += r.total || 0;
    });
    return {
      ...existingResult,
      ...computeScore(points, total),
      breakdown: submissionResults,
    };
  });
}
